/**
 * Employee Benefits Page
 *
 * Comprehensive benefits view with tabs:
 * - Program Benefits (available programs and enrollment)
 * - Claims & EOBs (transactions, claim history)
 * - HSA/FSA (account balances and transactions)
 * - ID Cards
 * - Dependents
 * - Providers
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Gift,
  FileText,
  DollarSign,
  Users,
  Building2,
  CreditCard,
  Search,
  Download,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  Clock,
  AlertCircle,
  X,
  ExternalLink,
  Heart,
  Eye,
  Stethoscope,
  Filter,
  Calendar,
  Receipt,
  ArrowUpDown,
  Loader2,
  Plus,
} from 'lucide-react';
import { getMemberData, getMemberToken } from '@/lib/memberAuth';
import { getWeaveinApiUrl } from '@/src/config';
import { useSubtabNavigation } from '@/hooks/useSubtabNavigation';
import { formatDateOnly } from '@/utils/dateTime';
import EmployeePortalLayout from '../layout';

// Types
interface BenefitProgram {
  id: number;
  program_code: string;
  program_name: string;
  program_type: string;
  program_category: string;
  description: string;
  status: string;
  enrollment_status?: string;
  vendor_name?: string;
}

interface Claim {
  id: number;
  claim_number: string;
  service_date: string;
  received_date: string;
  provider_name: string;
  provider_type: string;
  service_type: string;
  diagnosis_codes?: string[];
  procedure_codes?: string[];
  billed_amount: number;
  allowed_amount: number;
  plan_paid: number;
  patient_responsibility: number;
  deductible_applied: number;
  copay_amount: number;
  coinsurance_amount: number;
  status: 'processed' | 'pending' | 'denied' | 'in_review';
  eob_available: boolean;
  eob_url?: string;
}

interface AccountTransaction {
  id: number;
  transaction_date: string;
  description: string;
  merchant?: string;
  category: string;
  amount: number;
  transaction_type: 'debit' | 'credit' | 'contribution' | 'employer_contribution';
  status: 'posted' | 'pending';
  receipt_url?: string;
}

interface BenefitAccount {
  account_type: 'HSA' | 'FSA' | 'HRA' | 'DCFSA';
  balance: number;
  available_balance: number;
  ytd_contributions: number;
  employer_contributions: number;
  annual_limit: number;
  use_by_date?: string;
  account_number_last4?: string;
  transactions: AccountTransaction[];
}

interface Dependent {
  id: number;
  first_name: string;
  last_name: string;
  relationship: string;
  date_of_birth: string;
  ssn_last4?: string;
  covered_plans: string[];
  status: 'active' | 'pending' | 'terminated';
}

interface IDCard {
  plan_type: 'medical' | 'dental' | 'vision';
  carrier_name: string;
  member_id: string;
  group_number: string;
  plan_name: string;
  pcp_name?: string;
  pcp_phone?: string;
  effective_date: string;
  card_image_url?: string;
}

const tabs = [
  { id: 'programs', label: 'Program Benefits', icon: Gift },
  { id: 'claims', label: 'Claims & EOBs', icon: FileText },
  { id: 'accounts', label: 'HSA/FSA', icon: DollarSign },
  { id: 'id-cards', label: 'ID Cards', icon: CreditCard },
  { id: 'dependents', label: 'Dependents', icon: Users },
  { id: 'providers', label: 'Find Providers', icon: Building2 },
];

export default function EmployeeBenefitsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'programs');
  useSubtabNavigation(setActiveTab, ['programs', 'claims', 'accounts', 'id-cards', 'dependents', 'providers'] as const);
  const [loading, setLoading] = useState(true);
  const [employeeId, setEmployeeId] = useState<number | null>(null);

  // Data states
  const [programs, setPrograms] = useState<BenefitProgram[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [accounts, setAccounts] = useState<BenefitAccount[]>([]);
  const [dependents, setDependents] = useState<Dependent[]>([]);
  const [idCards, setIdCards] = useState<IDCard[]>([]);

  // Filter states
  const [claimFilter, setClaimFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: '',
    end: '',
  });

  const member = getMemberData();

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  useEffect(() => {
    const fetchBenefitsData = async () => {
      if (!member?.id) {
        setLoading(false);
        return;
      }

      try {
        const token = getMemberToken();
        if (!token) {
          setLoading(false);
          return;
        }

        // Fetch employee ID
        const employeeResponse = await fetch(
          getWeaveinApiUrl(`/api/employees/by-member/${member.id}`),
          {
            headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
          }
        );

        if (employeeResponse.ok) {
          const empData = await employeeResponse.json();
          setEmployeeId(empData.id);

          // Fetch benefits programs
          const programsResponse = await fetch(
            getWeaveinApiUrl('/api/member-portal/benefits'),
            {
              headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
            }
          );
          if (programsResponse.ok) {
            setPrograms(await programsResponse.json());
          }

          // Fetch claims (would be real API)
          try {
            const claimsResponse = await fetch(
              getWeaveinApiUrl(`/api/employees/${empData.id}/claims`),
              {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }
            );
            if (claimsResponse.ok) {
              setClaims(await claimsResponse.json());
            } else {
              // Mock data
              setClaims([
                {
                  id: 1,
                  claim_number: 'CLM-2025-001234',
                  service_date: '2025-11-15',
                  received_date: '2025-11-18',
                  provider_name: 'Dr. Sarah Johnson, MD',
                  provider_type: 'Primary Care',
                  service_type: 'Office Visit - Established Patient',
                  diagnosis_codes: ['Z00.00'],
                  procedure_codes: ['99213'],
                  billed_amount: 150.00,
                  allowed_amount: 125.00,
                  plan_paid: 100.00,
                  patient_responsibility: 25.00,
                  deductible_applied: 0,
                  copay_amount: 25.00,
                  coinsurance_amount: 0,
                  status: 'processed',
                  eob_available: true,
                },
                {
                  id: 2,
                  claim_number: 'CLM-2025-001189',
                  service_date: '2025-10-28',
                  received_date: '2025-11-02',
                  provider_name: 'Quest Diagnostics',
                  provider_type: 'Laboratory',
                  service_type: 'Comprehensive Metabolic Panel',
                  diagnosis_codes: ['R73.03'],
                  procedure_codes: ['80053'],
                  billed_amount: 280.00,
                  allowed_amount: 180.00,
                  plan_paid: 135.00,
                  patient_responsibility: 45.00,
                  deductible_applied: 25.00,
                  copay_amount: 0,
                  coinsurance_amount: 20.00,
                  status: 'processed',
                  eob_available: true,
                },
                {
                  id: 3,
                  claim_number: 'CLM-2025-001156',
                  service_date: '2025-10-15',
                  received_date: '2025-10-16',
                  provider_name: 'CVS Pharmacy #4521',
                  provider_type: 'Pharmacy',
                  service_type: 'Prescription - Metformin 500mg',
                  procedure_codes: ['NDC:00093-7212'],
                  billed_amount: 85.00,
                  allowed_amount: 85.00,
                  plan_paid: 70.00,
                  patient_responsibility: 15.00,
                  deductible_applied: 0,
                  copay_amount: 15.00,
                  coinsurance_amount: 0,
                  status: 'processed',
                  eob_available: true,
                },
                {
                  id: 4,
                  claim_number: 'CLM-2025-001298',
                  service_date: '2025-11-20',
                  received_date: '2025-11-22',
                  provider_name: 'City Hospital Radiology',
                  provider_type: 'Radiology',
                  service_type: 'X-Ray - Chest PA/Lateral',
                  diagnosis_codes: ['R05.9'],
                  procedure_codes: ['71046'],
                  billed_amount: 450.00,
                  allowed_amount: 320.00,
                  plan_paid: 0,
                  patient_responsibility: 320.00,
                  deductible_applied: 320.00,
                  copay_amount: 0,
                  coinsurance_amount: 0,
                  status: 'pending',
                  eob_available: false,
                },
              ]);
            }
          } catch (err) {
            console.error('Error fetching claims:', err);
          }

          // Fetch benefit accounts
          try {
            const accountsResponse = await fetch(
              getWeaveinApiUrl(`/api/employees/${empData.id}/benefit-accounts`),
              {
                headers: { 'Authorization': `Bearer ${token}`, 'X-CBO-ID': localStorage.getItem('active_cbo_id') || '', 'Content-Type': 'application/json' },
              }
            );
            if (accountsResponse.ok) {
              setAccounts(await accountsResponse.json());
            } else {
              // Mock data
              setAccounts([
                {
                  account_type: 'HSA',
                  balance: 2847.50,
                  available_balance: 2847.50,
                  ytd_contributions: 1800,
                  employer_contributions: 500,
                  annual_limit: 4150,
                  account_number_last4: '4521',
                  transactions: [
                    { id: 1, transaction_date: '2025-12-01', description: 'Payroll Contribution', merchant: '', category: 'Contribution', amount: 150, transaction_type: 'contribution', status: 'posted' },
                    { id: 2, transaction_date: '2025-11-28', description: 'CVS Pharmacy', merchant: 'CVS Pharmacy', category: 'Prescription', amount: -35.50, transaction_type: 'debit', status: 'posted' },
                    { id: 3, transaction_date: '2025-11-15', description: 'Dr. Johnson Office', merchant: 'Johnson Medical', category: 'Office Visit', amount: -25, transaction_type: 'debit', status: 'posted' },
                    { id: 4, transaction_date: '2025-11-01', description: 'Payroll Contribution', merchant: '', category: 'Contribution', amount: 150, transaction_type: 'contribution', status: 'posted' },
                    { id: 5, transaction_date: '2025-10-15', description: 'Employer Contribution', merchant: '', category: 'Employer Match', amount: 83.33, transaction_type: 'employer_contribution', status: 'posted' },
                  ],
                },
                {
                  account_type: 'FSA',
                  balance: 1450.00,
                  available_balance: 1450.00,
                  ytd_contributions: 2850,
                  employer_contributions: 0,
                  annual_limit: 3050,
                  use_by_date: '2026-03-15',
                  account_number_last4: '8734',
                  transactions: [
                    { id: 1, transaction_date: '2025-11-20', description: 'Walgreens - OTC Items', merchant: 'Walgreens', category: 'OTC Medical', amount: -45.99, transaction_type: 'debit', status: 'posted' },
                    { id: 2, transaction_date: '2025-11-05', description: 'Quest Diagnostics Copay', merchant: 'Quest Diagnostics', category: 'Lab Work', amount: -45, transaction_type: 'debit', status: 'posted' },
                    { id: 3, transaction_date: '2025-10-25', description: 'LensCrafters', merchant: 'LensCrafters', category: 'Vision', amount: -215, transaction_type: 'debit', status: 'posted' },
                  ],
                },
              ]);
            }
          } catch (err) {
            console.error('Error fetching accounts:', err);
          }

          // Mock dependents
          setDependents([
            { id: 1, first_name: 'Jane', last_name: 'Doe', relationship: 'Spouse', date_of_birth: '1985-03-15', ssn_last4: '1234', covered_plans: ['Medical', 'Dental', 'Vision'], status: 'active' },
            { id: 2, first_name: 'Tommy', last_name: 'Doe', relationship: 'Child', date_of_birth: '2015-08-22', ssn_last4: '5678', covered_plans: ['Medical', 'Dental', 'Vision'], status: 'active' },
            { id: 3, first_name: 'Sarah', last_name: 'Doe', relationship: 'Child', date_of_birth: '2018-01-10', ssn_last4: '9012', covered_plans: ['Medical', 'Dental', 'Vision'], status: 'active' },
          ]);

          // Mock ID Cards
          setIdCards([
            { plan_type: 'medical', carrier_name: 'Blue Shield of California', member_id: 'XYZ123456789', group_number: 'GRP-12345', plan_name: 'Gold PPO Plan', pcp_name: 'Dr. Sarah Johnson', pcp_phone: '(555) 123-4567', effective_date: '2025-01-01' },
            { plan_type: 'dental', carrier_name: 'Delta Dental', member_id: 'DD987654321', group_number: 'DEN-54321', plan_name: 'Delta Dental PPO', effective_date: '2025-01-01' },
            { plan_type: 'vision', carrier_name: 'VSP Vision Care', member_id: 'VSP456789123', group_number: 'VIS-98765', plan_name: 'VSP Choice Plan', effective_date: '2025-01-01' },
          ]);
        }
      } catch (error) {
        console.error('Error fetching benefits data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBenefitsData();
  }, [member?.id]);

  // Filter claims
  const filteredClaims = claims.filter(claim => {
    if (claimFilter !== 'all' && claim.status !== claimFilter) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        claim.provider_name.toLowerCase().includes(search) ||
        claim.service_type.toLowerCase().includes(search) ||
        claim.claim_number.toLowerCase().includes(search)
      );
    }
    return true;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'processed': return 'bg-green-100 text-green-700';
      case 'pending': return 'bg-amber-100 text-amber-700';
      case 'denied': return 'bg-red-100 text-red-700';
      case 'in_review': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <EmployeePortalLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </EmployeePortalLayout>
    );
  }

  return (
    <EmployeePortalLayout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Benefits</h1>
          <p className="text-gray-600 mt-1">
            View your program benefits, claims, accounts, and coverage details.
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200 overflow-x-auto">
            <nav className="flex -mb-px" aria-label="Tabs">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`
                      flex items-center px-4 py-4 text-sm font-medium border-b-2 whitespace-nowrap
                      ${isActive
                        ? 'border-indigo-600 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }
                    `}
                  >
                    <Icon className={`w-4 h-4 mr-2 ${isActive ? 'text-indigo-600' : 'text-gray-400'}`} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-6">
            {/* Program Benefits Tab */}
            {activeTab === 'programs' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">{programs.length} benefit programs available</p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search programs..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {programs
                    .filter(p => !searchTerm || p.program_name.toLowerCase().includes(searchTerm.toLowerCase()))
                    .map((program) => {
                      const isEnrolled = program.enrollment_status === 'ENROLLED' || program.enrollment_status === 'ACTIVE';
                      return (
                        <div
                          key={program.id}
                          className={`p-4 border rounded-xl transition-all ${
                            isEnrolled ? 'border-green-300 bg-green-50' : 'border-gray-200 hover:border-indigo-300'
                          }`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="font-semibold text-gray-900">{program.program_name}</h3>
                            {isEnrolled && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded-full flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Enrolled
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                            {program.description || 'Benefit program available to employees.'}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {program.program_category?.replace('_', ' ')}
                            </span>
                            <button className="text-sm text-indigo-600 font-medium hover:text-indigo-700">
                              View Details
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {/* Claims & EOBs Tab */}
            {activeTab === 'claims' && (
              <div className="space-y-6">
                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search claims by provider or service..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <select
                    value={claimFilter}
                    onChange={(e) => setClaimFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-200 rounded-lg bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Claims</option>
                    <option value="processed">Processed</option>
                    <option value="pending">Pending</option>
                    <option value="denied">Denied</option>
                  </select>
                </div>

                {/* Claims List */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Service Date</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Provider</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Service</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Billed</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">You Owe</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">EOB</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredClaims.map((claim) => (
                        <tr key={claim.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {formatDateOnly(claim.service_date)}
                          </td>
                          <td className="px-4 py-3">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{claim.provider_name}</p>
                              <p className="text-xs text-gray-500">{claim.provider_type}</p>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 max-w-xs truncate">
                            {claim.service_type}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900 text-right">
                            ${claim.billed_amount.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">
                            ${claim.patient_responsibility.toFixed(2)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(claim.status)}`}>
                              {claim.status.charAt(0).toUpperCase() + claim.status.slice(1)}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {claim.eob_available ? (
                              <button className="text-indigo-600 hover:text-indigo-700">
                                <Download className="w-4 h-4" />
                              </button>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {filteredClaims.length === 0 && (
                  <div className="text-center py-12">
                    <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">No claims found matching your criteria.</p>
                  </div>
                )}
              </div>
            )}

            {/* HSA/FSA Tab */}
            {activeTab === 'accounts' && (
              <div className="space-y-6">
                {accounts.map((account) => (
                  <div key={account.account_type} className="border border-gray-200 rounded-xl overflow-hidden">
                    {/* Account Header */}
                    <div className={`p-4 ${account.account_type === 'HSA' ? 'bg-green-50' : 'bg-purple-50'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${account.account_type === 'HSA' ? 'bg-green-100' : 'bg-purple-100'}`}>
                            <DollarSign className={`w-5 h-5 ${account.account_type === 'HSA' ? 'text-green-600' : 'text-purple-600'}`} />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{account.account_type} Account</h3>
                            <p className="text-sm text-gray-600">
                              Account ending in {account.account_number_last4 || '****'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Available Balance</p>
                          <p className={`text-2xl font-bold ${account.account_type === 'HSA' ? 'text-green-700' : 'text-purple-700'}`}>
                            ${account.available_balance.toFixed(2)}
                          </p>
                        </div>
                      </div>

                      {/* Account Stats */}
                      <div className="grid grid-cols-3 gap-4 mt-4">
                        <div className="text-center p-2 bg-white/50 rounded-lg">
                          <p className="text-xs text-gray-600">YTD Contributions</p>
                          <p className="font-semibold text-gray-900">${account.ytd_contributions}</p>
                        </div>
                        <div className="text-center p-2 bg-white/50 rounded-lg">
                          <p className="text-xs text-gray-600">Employer Match</p>
                          <p className="font-semibold text-gray-900">${account.employer_contributions}</p>
                        </div>
                        <div className="text-center p-2 bg-white/50 rounded-lg">
                          <p className="text-xs text-gray-600">Annual Limit</p>
                          <p className="font-semibold text-gray-900">${account.annual_limit}</p>
                        </div>
                      </div>

                      {account.use_by_date && (
                        <div className="mt-3 p-2 bg-amber-100 rounded-lg flex items-center">
                          <AlertCircle className="w-4 h-4 text-amber-600 mr-2" />
                          <span className="text-sm text-amber-800">
                            Use by {new Date(account.use_by_date).toLocaleDateString()} or funds may be forfeited
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Transactions */}
                    <div className="p-4">
                      <h4 className="font-medium text-gray-900 mb-3">Recent Transactions</h4>
                      <div className="space-y-2">
                        {account.transactions.map((tx) => (
                          <div key={tx.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div className="flex items-center space-x-3">
                              <div className={`w-2 h-2 rounded-full ${
                                tx.transaction_type === 'debit' ? 'bg-red-400' :
                                tx.transaction_type === 'contribution' ? 'bg-green-400' :
                                'bg-blue-400'
                              }`} />
                              <div>
                                <p className="text-sm font-medium text-gray-900">{tx.description}</p>
                                <p className="text-xs text-gray-500">{new Date(tx.transaction_date).toLocaleDateString()} • {tx.category}</p>
                              </div>
                            </div>
                            <span className={`font-semibold ${tx.amount < 0 ? 'text-red-600' : 'text-green-600'}`}>
                              {tx.amount < 0 ? '-' : '+'}${Math.abs(tx.amount).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <button className="w-full mt-4 py-2 text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center justify-center">
                        View All Transactions <ChevronRight className="w-4 h-4 ml-1" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ID Cards Tab */}
            {activeTab === 'id-cards' && (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {idCards.map((card) => {
                  const Icon = card.plan_type === 'medical' ? Heart : card.plan_type === 'dental' ? Stethoscope : Eye;
                  const bgColor = card.plan_type === 'medical' ? 'from-blue-600 to-blue-800' :
                                  card.plan_type === 'dental' ? 'from-teal-600 to-teal-800' :
                                  'from-indigo-600 to-indigo-800';
                  return (
                    <div key={card.plan_type} className={`rounded-xl overflow-hidden shadow-lg`}>
                      <div className={`bg-gradient-to-br ${bgColor} text-white p-4`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <Icon className="w-5 h-5" />
                            <span className="font-semibold capitalize">{card.plan_type} Insurance</span>
                          </div>
                          <button className="p-1 hover:bg-white/20 rounded">
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                        <p className="text-lg font-bold mb-1">{card.carrier_name}</p>
                        <p className="text-sm opacity-90">{card.plan_name}</p>
                      </div>
                      <div className="bg-white p-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Member ID</span>
                          <span className="font-mono font-medium">{card.member_id}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Group #</span>
                          <span className="font-mono font-medium">{card.group_number}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Effective</span>
                          <span className="font-medium">{new Date(card.effective_date).toLocaleDateString()}</span>
                        </div>
                        {card.pcp_name && (
                          <div className="pt-2 border-t mt-2">
                            <p className="text-xs text-gray-500">Primary Care Provider</p>
                            <p className="text-sm font-medium">{card.pcp_name}</p>
                            {card.pcp_phone && <p className="text-sm text-indigo-600">{card.pcp_phone}</p>}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Dependents Tab */}
            {activeTab === 'dependents' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-gray-600">{dependents.length} covered dependents</p>
                  <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 flex items-center">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Dependent
                  </button>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dependents.map((dep) => (
                    <div key={dep.id} className="border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center">
                            <span className="text-indigo-600 font-semibold">
                              {dep.first_name[0]}{dep.last_name[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{dep.first_name} {dep.last_name}</p>
                            <p className="text-sm text-gray-500">{dep.relationship}</p>
                          </div>
                        </div>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          dep.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {dep.status}
                        </span>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date of Birth</span>
                          <span className="font-medium">{new Date(dep.date_of_birth).toLocaleDateString()}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">SSN</span>
                          <span className="font-medium">***-**-{dep.ssn_last4}</span>
                        </div>
                        <div className="pt-2">
                          <p className="text-gray-600 mb-1">Covered Plans</p>
                          <div className="flex flex-wrap gap-1">
                            {dep.covered_plans.map((plan) => (
                              <span key={plan} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                {plan}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Find Providers Tab */}
            {activeTab === 'providers' && (
              <div className="space-y-6">
                <div className="text-center py-12">
                  <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Find In-Network Providers</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Search for doctors, hospitals, and specialists in your plan's network.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <a
                      href="https://www.blueshieldca.com/find-a-doctor"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Heart className="w-4 h-4 mr-2" />
                      Medical Providers
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                    <a
                      href="https://www.deltadental.com/find-a-dentist"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                    >
                      <Stethoscope className="w-4 h-4 mr-2" />
                      Dental Providers
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                    <a
                      href="https://www.vsp.com/find-a-doctor"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Vision Providers
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </EmployeePortalLayout>
  );
}
